import { useState } from "react";
import { Visit } from "../types/visit";
import { Button } from "../../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { PrescriptionForm } from "./PrescriptionForm";
import { SymptomsList } from "./SymptomsList";
import { InventoryUsageForm } from "./InventoryUsageForm";
import { ScheduleFollowUpDialog } from "./ScheduleFollowUpDialog";
import { SchedulePhysicianReminderDialog } from "./SchedulePhysicianReminderDialog";
import { useCreateVisit } from "../hooks/useCreateVisit";
import { useToast } from "../../../hooks/use-toast";
import { VisitInfoHeader } from "./VisitInfoHeader";
import { VitalsForm } from "./VitalsForm";
import { DiagnosisSection } from "./DiagnosisSection";
import { NotesSection } from "./NotesSection";

interface ActiveVisitFormProps {
  visit: Visit;
  onSave: (data: Partial<Visit>) => void;
  isSaving: boolean;
}

export function ActiveVisitForm({ visit, onSave, isSaving }: ActiveVisitFormProps) {
  const createVisit = useCreateVisit();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    symptoms: visit.symptoms || [],
    vitals: visit.vitals || {},
    diagnosis: visit.diagnosis || "",
    medicalHistory: visit.patient?.medicalHistory || "",
    prescriptions: visit.prescriptions || [],
    notes: visit.notes || "",
    inventoryUsage: visit.inventoryUsage || [],
    prescriptionChanges: visit.prescriptionChanges || [],
  });

  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  const handlePrescriptionStatusChange = (prescription: {
    id: string;
    medication: string;
    active: boolean;
  }) => {
    setFormData((prev) => ({
      ...prev,
      prescriptionChanges: [
        ...prev.prescriptionChanges,
        {
          prescriptionId: prescription.id,
          medication: prescription.medication,
          changeType: prescription.active ? "activated" : "deactivated",
          timestamp: new Date().toISOString(),
        },
      ],
    }));
  };

  const handleSave = () => {
    onSave({
      ...formData,
      status: "completed",
    });
  };

  const handleCancelReminder = (reminderId: string) => {
    const updatedReminders = visit.physicianReminders?.filter(
      (r) => r.id !== reminderId
    );
    onSave({
      physicianReminders: updatedReminders,
    });
  };

  return (
    <div className="space-y-6">
      <VisitInfoHeader visit={visit} />

      <Tabs defaultValue="vitals" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="vitals">Vitals</TabsTrigger>
          <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
          <TabsTrigger value="prescription">Prescription</TabsTrigger>
          <TabsTrigger value="inventory">Items Used</TabsTrigger>
        </TabsList>

        <ScrollArea className="h-[calc(90vh-12rem)]">
          <div className="p-6 space-y-6">
            <TabsContent value="vitals">
              <VitalsForm
                vitals={formData.vitals}
                onChange={(vitals) => setFormData({ ...formData, vitals })}
              />
            </TabsContent>

            <TabsContent value="symptoms">
              <SymptomsList
                symptoms={formData.symptoms}
                onChange={(symptoms) => setFormData({ ...formData, symptoms })}
              />
            </TabsContent>

            <TabsContent value="notes">
              <NotesSection
                initialNotes={visit.initialNotes}
                notes={formData.notes}
                onChange={(notes) => setFormData({ ...formData, notes })}
              />
            </TabsContent>

            <TabsContent value="diagnosis">
              <DiagnosisSection
                visit={visit}
                diagnosis={formData.diagnosis}
                medicalHistory={formData.medicalHistory}
                onDiagnosisChange={(diagnosis) =>
                  setFormData({ ...formData, diagnosis })
                }
                onMedicalHistoryChange={(medicalHistory) =>
                  setFormData({ ...formData, medicalHistory })
                }
                onShowFollowUpDialog={() => setShowFollowUpDialog(true)}
                onShowReminderDialog={() => setShowReminderDialog(true)}
                onCancelReminder={handleCancelReminder}
              />
            </TabsContent>

            <TabsContent value="prescription">
              <PrescriptionForm
                value={formData.prescriptions}
                onChange={(prescriptions) =>
                  setFormData({ ...formData, prescriptions })
                }
                visit={visit}
                patientId={visit.patientId}
                onPrescriptionStatusChange={handlePrescriptionStatusChange}
              />
            </TabsContent>

            <TabsContent value="inventory">
              <InventoryUsageForm
                value={formData.inventoryUsage}
                onChange={(inventoryUsage) =>
                  setFormData({ ...formData, inventoryUsage })
                }
              />
            </TabsContent>
          </div>
        </ScrollArea>
      </Tabs>

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={() => onSave(formData)}>
          Save Draft
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Completing..." : "Complete Visit"}
        </Button>
      </div>

      <ScheduleFollowUpDialog
        open={showFollowUpDialog}
        onClose={() => setShowFollowUpDialog(false)}
        currentVisit={visit}
        onSchedule={(data) => {
          createVisit.mutate(data, {
            onSuccess: () => {
              setShowFollowUpDialog(false);
              toast({
                title: "Follow-up Scheduled",
                description: "A new follow-up visit has been scheduled.",
              });
            },
            onError: () => {
              toast({
                title: "Error",
                description: "Failed to schedule follow-up visit. Please try again.",
                variant: "destructive",
              });
            },
          });
        }}
      />

      <SchedulePhysicianReminderDialog
        open={showReminderDialog}
        onClose={() => setShowReminderDialog(false)}
        currentVisit={visit}
      />
    </div>
  );
}
